(ns manage.adset-preview-panel.view.templates
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["react-masonry-component$default" :as Masonry]
            [common.dom :as dom]
            [common.widgets.checkbox :refer [checkbox]]
            [common.widgets.dropdown :refer [dropdown]]
            [common.widgets.template-preview :as template-preview]
            [manage.adset-preview-panel.db.templates :as db]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui visibility-checkbox
  "Hiccup for toggle visibility of a checkbox"
  [{:template/keys [id name]}]
  (let [visible (use-subscribe [:adset-preview.templates/visible? id])
        on-change #(rf/dispatch [(if visible
                                   :adset-preview.templates/hide
                                   :adset-preview.templates/show) id])]
    ($ checkbox
       {:label name
        :on-change on-change
        :value visible
        :data-testid (str "checkbox-" name)})))

(defui templates-filter
  "Hiccup filtering templates"
  []
  (let [items (use-subscribe [:adset-preview.templates/value :templates []])
        visible-ids (use-subscribe [:adset-preview.templates/value :visible-template-ids])]
    ($ Stack {:spacing 1.5}
       ($ Stack {:direction "row" :flexWrap "wrap" :gap 1}
          ($ Button
             {:startIcon ($ Icon "visibility_off")
              :disabled (zero? (count visible-ids))
              :onClick #(rf/dispatch [:adset-preview.templates/hide-all])
              :variant "text"
              :data-testid "hide-all-previews"}
             "Hide all previews")
          ($ Button
             {:startIcon ($ Icon "visibility")
              :disabled (every? visible-ids (db/templates->template-ids items))
              :onClick #(rf/dispatch [:adset-preview.templates/show-all])
              :variant "text"
              :data-testid "show-all-previews"}
             "Show all previews"))

       ($ Stack
          {:component "ul"
           :direction "row"
           :spacing 2
           :pl 0
           :sx #js {:listStyle "none"}}
          (for [item items
                :when item]
            ($ :li {:key (:template/id item)}
               ($ visibility-checkbox item)))))))

(defui templates-settings
  "Hiccup settings templates"
  [{:keys [mobile?]}]
  (let [auto-replay? (use-subscribe [:adset-preview.templates/value :auto-replay? false])
        product-selectors (use-subscribe [:adset-preview.products/value :product-selectors])
        sidebar-visible (use-subscribe [:adset-preview.core/value :sidebar-visible])
        zoom-level (use-subscribe [:adset-preview.templates/value :zoom-level])]
    ($ Stack
       {:direction "row"
        :justifyContent "space-between"
        :flexWrap "wrap"
        :gap 2}
       ($ Stack
          {:direction "row"
           :alignItems "center"
           :spacing 1
           :flexGrow 1}
          (when-not mobile?
            ($ Typography {:component "label" :font-weight "600"} "Settings:"))
          ($ Button
             {:startIcon ($ Icon "menu")
              :disabled (= :context sidebar-visible)
              :onClick #(rf/dispatch [:adset-preview.core/toggle-sidebar :context])
              :data-testid "open-sidebar-button"}
             "Context")

          (when (seq product-selectors)
            ($ Button
               {:startIcon ($ Icon "shopping_basket")
                :disabled (= :products sidebar-visible)
                :onClick #(rf/dispatch [:adset-preview.core/toggle-sidebar :products])}
               "Products")))

       ($ Stack {:direction "row" :gap 2}
          ($ Stack
             {:direction "row"
              :alignItems "center"
              :spacing 1}
             (when-not mobile?
               ($ Typography {:component "label" :font-weight "600"} "Preview:"))
             ($ Button
                {:startIcon ($ Icon "replay")
                 :onClick #(rf/dispatch [:adset-preview.templates/replay-all])
                 :data-testid :replay-all-button} "Replay all"))

          (when-not mobile?
            ($ checkbox
               {:on-change #(rf/dispatch [:adset-preview.templates/toggle-auto-replay %])
                :label "Auto-replay previews"
                :data-testid :auto-replay-previews-button
                :value auto-replay?})))

       (when-not mobile?
         (r/as-element
          [dropdown {:value zoom-level
                     :on-change #(rf/dispatch [:adset-preview.templates/zoom %])
                     :options [{:label "25%"
                                :value 25}
                               {:label "50%"
                                :value 50}
                               {:label "75%"
                                :value 75}
                               {:label "100%"
                                :value 100}]
                     :sx {:width "10rem"}
                     :data-testid "adset-preview-panel-templates-settings-zoom"}])))))

(defn- with-ref-animating-state
  "Higher order function to get state if ref is currently transitioning"
  [f sub]
  (fn [& props]
    (r/with-let [transitioning? (r/atom false)
                 ref (r/atom nil)
                 transitionend #(reset! transitioning? false)
                 set-ref (fn [node]
                           (reset! ref node)
                           (dom/register-transitionend node transitionend))
                 stored-sub-val (atom @(rf/subscribe sub))]
      (let [sub-val @(rf/subscribe sub)
            _ (when (not= @stored-sub-val sub-val)
                (reset! stored-sub-val sub-val)
                (reset! transitioning? true))]
        (apply f (conj props {:set-ref set-ref
                              :transitioning? transitioning?})))
      (finally
        (dom/unregister-transitionend @ref transitionend)))))

(defn template*
  [{:keys [set-ref transitioning?]}
   {:template/keys [id name width height] :as template} mobile?]
  (r/with-let [render-layout #(rf/dispatch
                               [:adset-preview.templates/render-masonry-layout])
               on-replay #(rf/dispatch [:iframe/reload [id]])
               body-ref #(when %
                           (set-ref %)
                           (dom/register-transitionend % render-layout))]

    (let [zoom-level @(rf/subscribe [:adset-preview.templates/value :zoom-level])
          visible @(rf/subscribe [:adset-preview.templates/visible? id])
          toggle-visibility #(rf/dispatch
                              [(if visible
                                 :adset-preview.templates/hide
                                 :adset-preview.templates/show) id])]
      [:> Stack
       {:sx (merge {:max-width "100%"
                    :width "max-content"
                    :mx 0
                    :mb 3}
                   (when mobile?
                     {:mx "auto"
                      :position "static !important"
                      :transition "none !important"
                      :transform "none !important"}))}

       [:> Stack {:direction "row" :justify-content "space-between" :align-items "center" :gap 1}
        [:> Typography {:font-weight "600" :data-testid :template-name-label} name]
        [:span {:data-testid :template-dimensions-label} (str width "x" height)]
        [:> Stack {:direction "row" :gap 1 :ml "auto"}
         [:> IconButton {:sx (when mobile?
                               {:display "none"})
                         :on-click toggle-visibility
                         :data-testid :toggle-visibility-button}
          [:> Icon {:sx {:fontSize "15px"}} (if visible "visibility_off" "visibility")]]
         [:> IconButton {:on-click on-replay}
          [:> Icon {:sx {:fontSize "15px"}
                    :data-test-id :replay-button} "replay"]]]]

       [template-preview/card
        {:scale (/ zoom-level 100)
         :ref body-ref
         :transitioning? transitioning?}
        template
        [:adset-preview.templates/iframe-ready id]]])))

(def template (-> template*
                  (with-ref-animating-state [:adset-preview.templates/value
                                             :zoom-level])))

(defn templates
  "Hiccup of templates container"
  [mobile? {:keys [title] :or {title "Templates"}}]
  (r/with-let [stored-mobile (atom mobile?)
               masonry-node-ref (fn [el]
                                  ;; El is nil on unmount.
                                  (rf/dispatch [:adset-preview.templates/register-masonry-instance (when el (.-masonry ^js/HTMLElement el))]))]

    (let [items @(rf/subscribe [:adset-preview.templates/visible-templates])
          has-templates? @(rf/subscribe [:adset-preview.templates/has-templates?])
          _ (when (not= @stored-mobile mobile?)
              (reset! stored-mobile mobile?)
              (rf/dispatch [:adset-preview.templates/render-masonry-layout]))]

      ($ Stack
         {:p 3
          :spacing 2
          :flexGrow 1
          :sx #js {:overflowY "auto"}
          :data-testid :adset-preview-panel-content}
         (when has-templates?
           ($ templates-settings {:mobile? mobile?}))
         ($ Typography {:variant "h2" :data-testid "preview-header-title"} title)
         (when (and (not mobile?) has-templates?)
           ($ templates-filter))

         (if has-templates?
           ($ Masonry
              {:options #js {:columnWidth 100
                             :gutter 24
                             :containerStyle #js {:position "relative" :flex "1 1"}}
               :ref masonry-node-ref
               :data-testid "templates-container"}
              (for [item items]
                (r/as-element
                 ^{:key (:template/id item)}
                 [template item mobile?])))
           ($ :p "No templates configured"))))))
